import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { useAppContext } from './AppContext';
import { getRouteConfig } from '../router/routeConfig';

interface HeaderState {
    title: string;
    subtitle?: string;
    action?: ReactNode;
}

interface HeaderContextType extends HeaderState {
    setHeader: (state: HeaderState) => void;
    setTitle: (title: string) => void;
    setSubtitle: (subtitle: string) => void;
    setAction: (action: ReactNode) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state, setState] = useState<HeaderState>({
        title: '',
        subtitle: undefined
    });

    const setHeader = useCallback((newState: HeaderState) => {
        setState(newState);
    }, []);

    const setTitle = useCallback((title: string) => {
        setState(prev => ({ ...prev, title }));
    }, []);

    const setSubtitle = useCallback((subtitle: string) => {
        setState(prev => ({ ...prev, subtitle }));
    }, []);

    const setAction = useCallback((action: ReactNode) => {
        setState(prev => ({ ...prev, action }));
    }, []);

    const { currentPage } = useAppContext();
    const defaultConfig = getRouteConfig(currentPage);

    // Reset custom state when page changes to ensure we use defaults unless overridden again
    useEffect(() => {
        setState({
            title: '',
            subtitle: undefined, // Reset to undefined so fallback works
            action: null
        });
    }, [currentPage]);

    // Derived values: use custom state if set (truthy), otherwise fallback to default config
    // Note: for title, we check if it's explicitly set to something non-empty? 
    // Actually, usePageHeader sets it immediately on mount.
    // If we want automatic defaults, we can just initialize state with defaults?
    // Or we return merged values.

    // Better logic: usePageHeader will overwrite this on mount.
    // But for pages WITHOUT usePageHeader, we want the default.
    // So if state.title is empty, use defaultConfig.title.

    const displayTitle = state.title || defaultConfig.title;
    const displaySubtitle = state.subtitle !== undefined ? state.subtitle : defaultConfig.subtitle;

    return (
        <HeaderContext.Provider value={{
            title: displayTitle,
            subtitle: displaySubtitle,
            action: state.action,
            setHeader,
            setTitle,
            setSubtitle,
            setAction
        }}>
            {children}
        </HeaderContext.Provider>
    );
};

export const useHeader = () => {
    const context = useContext(HeaderContext);
    if (!context) {
        throw new Error('useHeader must be used within a HeaderProvider');
    }
    return context;
};
